import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TokenService {
  private readonly ACCESS_TOKEN_KEY = 'ofac_access_token';
  private readonly ID_TOKEN_KEY = 'ofac_id_token';
  private readonly TOKEN_EXPIRY_KEY = 'ofac_token_expiry';

  /**
   * Stores the access token
   */
  setAccessToken(token: string, expiresIn: number): void {
    localStorage.setItem(this.ACCESS_TOKEN_KEY, token);
    const expiryTime = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiryTime.toString());
  }

  /**
   * Gets the access token
   */
  getAccessToken(): string | null {
    const token = localStorage.getItem(this.ACCESS_TOKEN_KEY);
    if (!token) {
      return null;
    }

    // Check if token is expired
    const expiryTime = localStorage.getItem(this.TOKEN_EXPIRY_KEY);
    if (expiryTime && Date.now() > parseInt(expiryTime, 10)) {
      this.clearTokens();
      return null;
    }

    return token;
  }

  /**
   * Stores the ID token
   */
  setIdToken(token: string): void {
    localStorage.setItem(this.ID_TOKEN_KEY, token);
  }

  /**
   * Gets the ID token
   */
  getIdToken(): string | null {
    return localStorage.getItem(this.ID_TOKEN_KEY);
  }

  /**
   * Checks if a valid token exists
   */
  hasValidToken(): boolean {
    return this.getAccessToken() !== null;
  }

  /**
   * Clears all tokens
   */
  clearTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.ID_TOKEN_KEY);
    localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
  }

  /**
   * Decodes a JWT token
   */
  decodeToken(token: string): any {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch {
      return null;
    }
  }

  /**
   * Gets claims from the access token (the token used to call the OFAC API).
   * Roles and OrganizationId are read from this token so they match what the API receives.
   */
  getTokenClaims(): any {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return null;
    }
    return this.decodeToken(accessToken);
  }

  /**
   * Gets claims from the ID token (fallback when access token does not include custom claims).
   */
  getIdTokenClaims(): any {
    const idToken = this.getIdToken();
    if (!idToken) {
      return null;
    }
    return this.decodeToken(idToken);
  }
}
